import * as Sentry from "@sentry/react";
import type { IApiClient } from "@/lib/__shared__/apiClient";
import {
	albums,
	type ApiResponse,
	collections,
	customBlendGroup,
	effects,
	freeGroup,
	favoritesGroup,
	tracks,
} from "@coffitivity/shared";
import type {
	AnyTrackModel,
	TrackModel,
	ResolvedTrackModel,
	ResolvedBlendItem,
	AudioGroupModel,
	GroupWithTracksModel,
	AlbumWithTracksModel,
	CollectionWithAlbumsModel,
	EffectWithCollectionsModel,
	CollectionWithTracksModel,
	FreeGroupModel,
	CollectionModel,
	EffectModel,
	ResolvedAudioGroupModel,
	NewBlendTrackData,
	FreeGroupByCollectionModel,
} from "@coffitivity/shared";
import { AUDIO_GROUP_TYPE, type AudioGroupType } from "@coffitivity/shared";

export interface UserProfile {
	id: string;
	data?: {
		favorites?: string[];
	};
	created_at?: string;
	updated_at?: string;
}

export interface PublicGroupsModel {
	free: FreeGroupModel;
	collections: CollectionModel[];
	effects: EffectModel[];
}

export interface RequestOptions {
	findBy?: "ID" | "SLUG";
}

type ApiFormErrors = {
	globalErrors: string[];
	fieldErrors: Record<string, string[]>;
};

type AudioRepositoryErrorOptions = {
	status?: number;
	code?: string;
	formErrors?: ApiFormErrors;
};

export class AudioRepositoryError extends Error {
	readonly status?: number;
	readonly code?: string;
	readonly formErrors?: ApiFormErrors;

	constructor(message: string, options: AudioRepositoryErrorOptions = {}) {
		super(message);
		this.name = "AudioRepositoryError";
		this.status = options.status;
		this.code = options.code;
		this.formErrors = options.formErrors;
	}
}

export interface IAudioRepository {
	createCustomBlend(userId: string): ResolvedTrackModel;
	deleteCustomBlend(
		_trackId: string,
		_options: RequestOptions,
	): Promise<boolean>;
	getAllSingleTracks(): Promise<Array<ResolvedTrackModel>>;
	getFavoritesGroupWithTracks(
		_trackIds: string[],
	): Promise<GroupWithTracksModel>;
	getFreeGroupByCollection(): Promise<FreeGroupByCollectionModel>;
	getFreeGroupWithTracks(): Promise<GroupWithTracksModel>;
	getFreeTracks(): Promise<Array<ResolvedTrackModel>>;
	getGroupsByIds(_groupIds: string[]): Promise<AudioGroupModel[]>;
	getPublicGroups(): Promise<PublicGroupsModel>;
	getManyTracks(
		_trackIds: string[],
		_options?: RequestOptions,
	): Promise<Array<ResolvedTrackModel>>;
	getRecentlyAdded(_limit: number): Promise<Array<ResolvedTrackModel>>;
	getTrack(_id: string): Promise<ResolvedTrackModel>;
	getTrackBySlug(
		_slug: string,
		_options?: RequestOptions,
	): Promise<ResolvedTrackModel>;
	getGroupAndTracksBySlug(
		_groupType: AudioGroupType,
		_groupSlug: string,
	): Promise<GroupWithTracksModel>;
	getCollectionWithAlbums(slug: string): Promise<CollectionWithAlbumsModel>;
	getEffectWithCollections(slug: string): Promise<EffectWithCollectionsModel>;
	listCustomBlends(): Promise<Array<ResolvedTrackModel>>;
	saveCustomBlend(
		_track: NewBlendTrackData,
		_options: RequestOptions,
	): Promise<ResolvedTrackModel>;
	updateCustomBlend(
		_trackId: string,
		_track: NewBlendTrackData,
		_options: RequestOptions,
	): Promise<ResolvedTrackModel>;
}

// Build a lookup map for O(1) track access
const trackMap = new Map<string, AnyTrackModel>(tracks.map((t) => [t.id, t]));

const trackBySlugMap = new Map<string, AnyTrackModel>(
	tracks.map((t) => [t.slug, t]),
);

// If we ever allow blends to contain any track that isn't stored on amp we'll need to
// handle this procedure on the server
function resolveTrack(track: AnyTrackModel): ResolvedTrackModel {
	if (track.type === "TRACK") {
		return track;
	}

	const resolvedBlend: ResolvedBlendItem[] = track.blend.map((item) => {
		const subTrack = trackMap.get(item.trackId);
		if (!subTrack) {
			throw new Error(
				`Blend sub-track not found: ${item.trackId} in blend ${track.id}`,
			);
		}

		if (subTrack.type !== "TRACK") {
			throw new Error(
				`Nested blend not supported: ${subTrack.id} in blend ${track.id}`,
			);
		}

		return {
			volume: item.volume,
			track: subTrack,
		};
	});

	return {
		...track,
		blend: resolvedBlend,
	};
}

export class AudioRepository implements IAudioRepository {
	private apiClient: IApiClient;

	constructor(apiClient: IApiClient) {
		this.apiClient = apiClient;
	}

	createCustomBlend(userId: string): ResolvedTrackModel {
		return {
			id: crypto.randomUUID(),
			name: "My New Blend",
			slug: `${userId}-${Date.now()}`,
			description: "",
			type: "BLEND",
			isPremium: false,
			isLooping: true,
			userId,
			imageUrlStub: "",
			albumId: "",
			collectionId: customBlendGroup.id,
			effectIds: [],
			location: null,
			releaseDate: new Date().toISOString(),
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
			blend: [],
		};
	}

	async getAllSingleTracks(): Promise<Array<ResolvedTrackModel>> {
		const singleTracks = tracks
			.filter((t) => t.type === "TRACK")
			.map(resolveTrack);

		return singleTracks;
	}

	async getFavoritesGroupWithTracks(
		_trackIds: string[],
	): Promise<GroupWithTracksModel> {
		const idSet = new Set(_trackIds);
		const groupTracks = tracks.filter((t) => idSet.has(t.id)).map(resolveTrack);

		return {
			...favoritesGroup,
			resolvedTracks: groupTracks,
		};
	}

	async getFreeGroupByCollection(): Promise<FreeGroupByCollectionModel> {
		const freeTracks = tracks.filter((t) => !t.isPremium);

		if (!freeTracks) throw new Error(`No free tracks found`);

		const tracksByCollection = new Map<string, AnyTrackModel[]>();
		for (const track of freeTracks) {
			if (!tracksByCollection.has(track.collectionId)) {
				tracksByCollection.set(track.collectionId, []);
			}
			tracksByCollection.get(track.collectionId)!.push(track);
		}

		const collectionsWithTracks: CollectionWithTracksModel[] = [];
		for (const [collectionId, collectionTracks] of tracksByCollection) {
			const collectionEntry = collections.find((c) => c.id === collectionId);
			if (!collectionEntry) continue;

			collectionsWithTracks.push({
				...collectionEntry,
				resolvedTracks: collectionTracks.map(resolveTrack),
			});
		}

		collectionsWithTracks.sort((a, b) => a.name.localeCompare(b.name));

		return { ...freeGroup, collections: collectionsWithTracks };
	}

	async getFreeGroupWithTracks(): Promise<GroupWithTracksModel> {
		const groupTracks = tracks
			.filter((t) => !t.isPremium)
			.sort(
				(a, b) =>
					new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime(),
			)
			.map((t) => resolveTrack(t));

		return {
			...freeGroup,
			resolvedTracks: groupTracks,
		};
	}

	async getFreeTracks(): Promise<Array<ResolvedTrackModel>> {
		const groupTracks = tracks
			.filter((t) => !t.isPremium)
			.sort(
				(a, b) =>
					new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime(),
			)
			.map((t) => resolveTrack(t));

		return groupTracks;
	}

	async getTrack(_id: string): Promise<ResolvedTrackModel> {
		const track = trackMap.get(_id);
		if (!track) throw new Error(`Track not found: ${_id}`);
		return resolveTrack(track);
	}

	async getTrackBySlug(
		_slug: string,
		_options: RequestOptions = {},
	): Promise<ResolvedTrackModel> {
		try {
			const localTrack = trackBySlugMap.get(_slug);
			if (localTrack) {
				return resolveTrack(localTrack);
			}

			// the track we're looking for isn't in our local dataset, it might be a user created blend so we ask the server for it
			const response = await this.apiClient.fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/audio/custom-blends/${_slug}?searchBy=slug`,
				{
					method: "GET",
				},
			);

			const json: { data: TrackModel; message?: string } =
				await response.json();

			if (response.ok) {
				return resolveTrack(json.data);
			}

			throw new Error(json.message ?? "Unknown error fetching track by slug.");
		} catch (error) {
			Sentry.captureException(error);
			return Promise.reject(`Track not found by slug: ${_slug}`);
		}
	}

	async getManyTracks(
		_trackIds: string[],
		_options: RequestOptions = {},
	): Promise<Array<ResolvedTrackModel>> {
		const idSet = new Set(_trackIds);

		// First, get all tracks available locally
		const localTracks = tracks.filter((t) => idSet.has(t.id)).map(resolveTrack);
		const localTrackMap = new Map(localTracks.map((track) => [track.id, track]));
		const foundIds = new Set(localTracks.map((t) => t.id));

		// Find IDs that weren't found locally
		const missingIds = _trackIds.filter((id) => !foundIds.has(id));

		if (missingIds.length === 0) {
			return _trackIds
				.map((trackId) => localTrackMap.get(trackId))
				.filter((track): track is ResolvedTrackModel => track !== undefined);
		}

		// Fetch missing tracks from the server (likely custom blends)
		const remoteTrackPromises = missingIds.map(async (id) => {
			try {
				const response = await this.apiClient.fetch(
					`${import.meta.env.VITE_USER_SERVER_URL}/audio/custom-blends/${id}${_options.findBy === "ID" ? "?searchBy=id" : "?searchBy=slug"}`,
					{
						method: "GET",
					},
				);

				const json: { data: TrackModel; message?: string } =
					await response.json();

				if (response.ok) {
					return resolveTrack(json.data);
				}

				return null;
			} catch (error) {
				Sentry.captureException(error);
				return null;
			}
		});

		const remoteTracks = (await Promise.all(remoteTrackPromises)).filter(
			(t): t is ResolvedTrackModel => t !== null,
		);
		const remoteTrackMap = new Map(
			remoteTracks.map((track) => [track.id, track]),
		);

		return _trackIds
			.map((trackId) => localTrackMap.get(trackId) ?? remoteTrackMap.get(trackId))
			.filter((track): track is ResolvedTrackModel => track !== undefined);
	}

	async getRecentlyAdded(_limit = 10): Promise<Array<ResolvedTrackModel>> {
		return [...tracks]
			.sort(
				(a, b) =>
					new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime(),
			)
			.slice(0, _limit)
			.map(resolveTrack);
	}

	async getPublicGroups(): Promise<PublicGroupsModel> {
		return {
			free: freeGroup,
			collections,
			effects,
		};
	}

	async getGroupsByIds(_groupIds: string[]): Promise<AudioGroupModel[]> {
		if (!_groupIds.length) return [];

		const idSet = new Set(_groupIds);
		const result: AudioGroupModel[] = [];
		const searchableGroups = [...collections, ...effects, customBlendGroup];

		for (const groupId of idSet) {
			const group = searchableGroups.find((g) => g.id === groupId);
			if (group) {
				result.push(group);
			}
		}

		return result;
	}

	async getGroupAndTracksBySlug(
		_groupType: AudioGroupType,
		_groupSlug: string,
	): Promise<ResolvedAudioGroupModel> {
		switch (_groupType) {
			// we don't fetch free group tracks but we could. Use getFreeTracks instead
			case AUDIO_GROUP_TYPE.COLLECTION: {
				// Search collections
				const collection = collections.find((c) => c.slug === _groupSlug);
				if (collection) {
					const groupTracks = tracks
						.filter((t) => t.collectionId === collection.id)
						.map((t) => resolveTrack(t));

					return {
						...collection,
						resolvedTracks: groupTracks,
					};
				}
				break;
			}
			case AUDIO_GROUP_TYPE.EFFECT: {
				// Search effects
				const effect = effects.find((e) => e.slug === _groupSlug);
				if (effect) {
					const groupTracks = tracks
						.filter((t) => t.effectIds.includes(effect.id))
						.map((t) => resolveTrack(t));

					return {
						...effect,
						resolvedTracks: groupTracks,
					};
				}
				break;
			}
			case AUDIO_GROUP_TYPE.ALBUM: {
				// Search albums
				const album = albums.find((a) => a.slug === _groupSlug);
				if (album) {
					const groupTracks = album.tracks
						.map((trackId) => {
							const track = trackMap.get(trackId);
							if (!track) return null;
							return resolveTrack(track);
						})
						.filter((t): t is ResolvedTrackModel => t !== null);

					return {
						...album,
						resolvedTracks: groupTracks,
					};
				}
				break;
			}
			default:
				throw new Error(`Invalid group type: ${_groupType}`);
		}

		throw new Error(`Group not found: ${_groupSlug}`);
	}

	async getCollectionWithAlbums(
		slug: string,
	): Promise<CollectionWithAlbumsModel> {
		const collection = collections.find((c) => c.slug === slug);
		if (!collection) throw new Error(`Collection not found: ${slug}`);

		const collectionTracks = tracks.filter(
			(t) => t.collectionId === collection.id,
		);

		const tracksByAlbum = new Map<string, AnyTrackModel[]>();
		for (const track of collectionTracks) {
			if (!tracksByAlbum.has(track.albumId)) {
				tracksByAlbum.set(track.albumId, []);
			}
			tracksByAlbum.get(track.albumId)!.push(track);
		}

		const albumsWithTracks: AlbumWithTracksModel[] = [];
		for (const [albumId, albumTracks] of tracksByAlbum) {
			const albumEntry = albums.find((a) => a.id === albumId);
			if (!albumEntry) continue;

			albumsWithTracks.push({
				...albumEntry,
				resolvedTracks: albumTracks.map(resolveTrack),
			});
		}

		albumsWithTracks.sort(
			(a, b) =>
				new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime(),
		);

		return { ...collection, albums: albumsWithTracks };
	}

	async getEffectWithCollections(
		slug: string,
	): Promise<EffectWithCollectionsModel> {
		const effect = effects.find((e) => e.slug === slug);
		if (!effect) throw new Error(`Effect not found: ${slug}`);

		const effectTracks = tracks.filter((t) => t.effectIds.includes(effect.id));

		const tracksByCollection = new Map<string, AnyTrackModel[]>();
		for (const track of effectTracks) {
			if (!tracksByCollection.has(track.collectionId)) {
				tracksByCollection.set(track.collectionId, []);
			}
			tracksByCollection.get(track.collectionId)!.push(track);
		}

		const collectionsWithTracks: CollectionWithTracksModel[] = [];
		for (const [collectionId, collectionTracks] of tracksByCollection) {
			const collectionEntry = collections.find((c) => c.id === collectionId);
			if (!collectionEntry) continue;

			collectionsWithTracks.push({
				...collectionEntry,
				resolvedTracks: collectionTracks.map(resolveTrack),
			});
		}

		collectionsWithTracks.sort((a, b) => a.name.localeCompare(b.name));

		return { ...effect, collections: collectionsWithTracks };
	}

	async listCustomBlends(): Promise<Array<ResolvedTrackModel>> {
		try {
			const response = await this.apiClient.fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/audio/custom-blends`,
				{
					method: "GET",
				},
			);

			const json: { data: Array<TrackModel>; message: string } =
				await response.json();

			if (response.ok) {
				const resolvedTracks = json.data.map((track) => resolveTrack(track));

				return resolvedTracks;
			}

			throw new Error(json.message);
		} catch (error) {
			Sentry.captureException(error);
			return Promise.reject(
				error instanceof Error
					? error.message
					: "An unexpected error occurred.",
			);
		}
	}

	async saveCustomBlend(
		_track: NewBlendTrackData,
		_options: RequestOptions = {}, // fallback to empty object if not provided for safety
	): Promise<ResolvedTrackModel> {
		try {
			const response = await this.apiClient.fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/audio/custom-blends`,
				{
					method: "POST",
					body: JSON.stringify(_track),
				},
			);

			const json: ApiResponse<TrackModel> = await response.json();

			console.log("Save custom blend response:", { response, json });

			if (response.ok && json.success) {
				return resolveTrack(json.data);
			}

			if (!json.success) {
				throw new AudioRepositoryError(
					json.message ?? "Failed to save custom blend.",
					{
						status: response.status,
						code: json.code,
						formErrors: json.formErrors,
					},
				);
			}

			throw new AudioRepositoryError("Failed to save custom blend.", {
				status: response.status,
			});
		} catch (error) {
			Sentry.captureException(error);
			console.log("Error saving custom blend:", error);

			if (error instanceof AudioRepositoryError) {
				return Promise.reject(error);
			}

			return Promise.reject(
				new AudioRepositoryError(
					error instanceof Error
						? error.message
						: "An unexpected error occurred.",
				),
			);
		}
	}

	async updateCustomBlend(
		_trackId: string,
		_track: NewBlendTrackData,
		_options: RequestOptions = {},
	): Promise<ResolvedTrackModel> {
		try {
			const response = await this.apiClient.fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/audio/custom-blends/${_trackId}`,
				{
					method: "PATCH",
					body: JSON.stringify(_track),
				},
			);

			const json: ApiResponse<TrackModel> = await response.json();

			if (response.ok && json.success) {
				return resolveTrack(json.data);
			}

			if (!json.success) {
				throw new AudioRepositoryError(
					json.message ?? "Failed to update custom blend.",
					{
						status: response.status,
						code: json.code,
						formErrors: json.formErrors,
					},
				);
			}

			throw new AudioRepositoryError("Failed to update custom blend.", {
				status: response.status,
			});
		} catch (error) {
			Sentry.captureException(error);

			if (error instanceof AudioRepositoryError) {
				return Promise.reject(error);
			}

			return Promise.reject(
				new AudioRepositoryError(
					error instanceof Error
						? error.message
						: "An unexpected error occurred.",
				),
			);
		}
	}

	async deleteCustomBlend(
		_trackId: string,
		_options: RequestOptions = {},
	): Promise<boolean> {
		try {
			const response = await this.apiClient.fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/audio/custom-blends/${_trackId}`,
				{
					method: "DELETE",
				},
			);

			const json: ApiResponse<{ deleted: boolean }> = await response.json();

			if (response.ok && json.success) {
				return true;
			}

			if (!json.success) {
				throw new AudioRepositoryError(
					json.message ?? "Failed to delete custom blend.",
					{
						status: response.status,
						code: json.code,
					},
				);
			}

			throw new AudioRepositoryError("Failed to delete custom blend.", {
				status: response.status,
			});
		} catch (error) {
			Sentry.captureException(error);

			if (error instanceof AudioRepositoryError) {
				return Promise.reject(error);
			}

			return Promise.reject(
				new AudioRepositoryError(
					error instanceof Error
						? error.message
						: "An unexpected error occurred.",
				),
			);
		}
	}
}
